import { Request, Response } from '@luxuryescapes/contract-svc-tour';

export type TourOfferSummary = Response.TourOfferSummary;

export type TourOfferDeparture = Response.TourOfferDeparture;

export type TourOfferFormat = Response.TourOfferFormat;
export type TourOfferBrand = Response.TourOfferBrand;
export type TourOfferOperations = Response.TourOfferOperations;
/** The single-traveller tours and default fine print arrive with contract 1.85.30 (svc-tour #2550). */
export type TourOfferFormOptions = Response.TourOfferFormOptions & {
  singlePaxTourIds?: Array<string>;
  defaultFinePrint?: { luxuryEscapes: Array<FinePrintItem>; trustedPartners: Array<FinePrintItem> };
};
export type TourOfferPaymentScheduleTemplate = Response.TourOfferPaymentScheduleTemplate;
export type TourOfferOptionalExperience = Response.TourOfferOptionalExperience;
export type TourOfferTravellersInput = Request.PutTourOfferTravellersBody;
export type TourOfferPricingLevel = Response.TourOfferPricingLevel;
export type TourOfferPricingPrice = Response.TourOfferPricingPrice;
export type TourOfferPricingDeparture = Response.TourOfferPricingDeparture;
export type TourOfferPricingOption = Response.TourOfferPricingOption;
export type TourOfferPricing = Response.TourOfferPricing;
export type TourOfferPricingInput = Request.PutTourOfferPricingBody;
export type TourOfferDepartureItinerary = Response.TourOfferDepartureItinerary;
export type TourOfferRelatedTour = Response.TourOfferRelatedTour;
export type TourOfferRelationships = Response.TourOfferRelationships;
export type TourOfferScheduleInput = Request.TourOfferScheduleInput;
export type TourOfferTransferType = Response.TourOfferTransferType;
export const TOUR_OFFER_TRANSFER_TYPE_LABELS: Record<TourOfferTransferType, string> = {
  included: 'Transfer included',
  transfersIncludedOverride: 'Transfer included (rules override)',
  notIncluded: 'Not included',
  includedEarlyHoursDeparture: 'Transfer included with early hours departure',
  includedExtendedAccommodation: 'Transfer included with extended accommodation',
};
export type TourOfferTransferInput = Request.TransferInformation;
export type TourOfferItineraryDayInput = Request.TourOfferItineraryDayInput;
export type TourOfferItineraryInput = Request.PutTourOfferConfigurationItineraryBody;
export type TourOfferHotelAddOns = Response.TourOfferHotelAddOns;
export type TourOfferConfigurationDetailsInput = Request.PutTourOfferConfigurationDetailsBody;
export type TourOfferRelationshipsInput = Request.PutTourOfferRelationshipsBody;
export type TourOfferContent = Response.TourOfferContent;
export type TourOffer = Response.TourOffer;
export type TourOfferNewDepartures = NonNullable<TourOffer['newDepartures']>;
export type TourOfferBookingTerms = Response.TourOfferBookingTerms;
export type FinePrintItem = TourOfferContent['finePrint'][number];
export type FinePrintCategory = FinePrintItem['category'];

export type TourOfferConfiguration = TourOffer['services'][number]['configurations'][number];
export type TourOfferItinerary = TourOfferConfiguration['itineraries'][number];
export type ConfigurationEntry = Response.ConfigurationEntry;
export type ConfigurationInclusion = Response.ConfigurationInclusion;
export type ConfigurationHighlight = Response.ConfigurationHighlight;
export type InclusionCategory = Request.InclusionCategory;

/** What svc-tour's configuration content route replaces: the copy an LTLE edits. */
export interface TourOfferConfigurationContent {
  title: string | null;
  description: string | null;
  inclusions: Array<ConfigurationEntry>;
  itineraries: Array<Pick<TourOfferItinerary, 'id' | 'title' | 'text' | 'accommodation' | 'meals'>>;
}

// svc-tour has no endpoint for these lists, so they're typed against the contract to stay in step.
export const TOUR_STYLE_LABELS: Record<Response.TourOfferStyle, string> = {
  deluxe: 'Deluxe',
  premium: 'Premium',
  ultraLux: 'Ultra lux',
  signatureSeries: 'Signature series',
  custom: 'Custom',
  insiderSeries: 'Insider series',
};

export const GUIDE_TYPE_LABELS: Record<Response.TourOfferGuideType, string> = {
  fullyGuided: 'Fully guided',
  partiallyGuided: 'Partially guided',
  selfGuided: 'Self guided',
};

export const ACTIVITY_LEVEL_LABELS: Record<Response.TourOfferActivityLevel, string> = {
  relaxed: 'Relaxed',
  moderate: 'Moderate',
  active: 'Active',
};

export const TOUR_OFFER_STATUS_LABELS: Record<Response.TourOfferStatus, string> = {
  draft: 'Draft',
  contentApproved: 'Content approved',
};

export const TOUR_OFFER_FORMAT_LABELS: Record<TourOfferFormat, string> = {
  group: 'Group tour',
  private: 'Private journey',
};

export const DEPOSIT_TYPE_LABELS: Record<Response.TourOfferDepositType, string> = {
  default: 'Default',
  flat: 'Flat amount',
  percentage: 'Percentage',
  noDeposit: 'No deposit',
};

export const FINE_PRINT_CATEGORY_LABELS: Record<FinePrintCategory, string> = {
  information: 'Important information',
  inclusions: "What's included",
  exclusions: "What's not included",
  dates: 'Dates',
  cancellations: 'Cancellations',
  transport: 'Transport',
  accommodation: 'Accommodation',
  food: 'Food',
  health: 'Health',
  vaccination: 'Vaccination',
  travel: 'Travel',
  destination: 'Destination',
  baggage_restrictions: 'Baggage restrictions',
  booking: 'Booking',
  surcharges: 'Surcharges',
  optional_extras: 'Optional experiences',
};

/** A new LTLE has no offer content yet; the editor starts from this. */
export const EMPTY_TOUR_OFFER_CONTENT: TourOfferContent = {
  tourStyle: null,
  guideType: null,
  activityLevel: null,
  categories: [],
  operatedBy: null,
  finePrint: [],
  hosts: [],
  hostsDefaultImage: null,
  sponsors: [],
  videoId: null,
  campaignTags: [],
  showPricePerDay: false,
  highlightPackages: false,
  format: 'group',
  emailSubjectLine: null,
  showValuedAtPrice: true,
};

export const EMPTY_TOUR_OFFER_BOOKING_TERMS: TourOfferBookingTerms = {
  depositType: null,
  depositAmount: null,
  depositDueDays: null,
  finalPaymentDueDays: null,
  scheduleTemplateIds: [],
  optionalExperienceLeadDays: null,
  dateChangeCutoffDays: null,
};

export type CreateLtlePayload = Request.CreateTourOfferLtleBody;

export type InclusionKind = Request.InclusionKind;

export const INCLUSION_KIND_LABELS: Record<InclusionKind, string> = {
  standard: 'Standard',
  highlight: 'Highlight',
  flash: 'Flash',
  luxPlus: 'LUX+ bonus',
};

export const INCLUSION_CATEGORY_LABELS: Record<InclusionCategory, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
  allInclusiveDining: 'All-inclusive dining',
  dailyDining: 'Daily dining',
  diningExperience: 'Dining experience',
  flightCredit: 'Flight credit',
  nightlyCocktail: 'Nightly cocktail',
  freeFlowDrinks: 'Free-flow drinks',
  freeFlowHour: 'Free-flow hour',
  welcomeDrink: 'Welcome drink',
  airportTransfer: 'Airport transfer',
  privateVipTransfer: 'Private VIP transfer',
  roundtripDomesticFlights: 'Return domestic flights',
  oneWayDomesticFlight: 'One-way domestic flight',
  oneWayInternationalFlight: 'One-way international flight',
  returnInternationalFlights: 'Return international flights',
  clubLoungeAccess: 'Club lounge access',
  taxesIncluded: 'Taxes included',
  accommodation: 'Accommodation',
  upgradedAccommodation: 'Upgraded accommodation',
  sightseeing: 'Sightseeing',
  museum: 'Museum',
  nature: 'Nature',
  iconicLandmark: 'Iconic landmark',
  cruise: 'Cruise',
  other: 'Other',
};
